import { db } from "@/lib/firebase";
import {
  contentTypeForFilename,
  MAX_UPLOAD_BYTES,
} from "@/lib/libraryFormats";
import { LibraryService } from "@/lib/libraryService";
import {
  LibraryDocument,
  safeLibraryDocumentParse,
} from "@/schemas/firestore/library.schema";
import { captureException } from "@sentry/react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { StateCreator } from "zustand";
import { StoreState } from "..";

export interface LibraryUploadProgress {
  filename: string;
  /** 0–1, from the XHR upload progress event. */
  fraction: number;
}

export interface LibrarySlice {
  libraryDocuments: LibraryDocument[];
  isLibraryLoading: boolean;
  libraryError: string | null;
  libraryUpload: LibraryUploadProgress | null;

  /**
   * Live-read the org's library.
   *
   * Direct-from-Firestore: the rules allow `isOrgMember` reads of this
   * collection, and core deliberately exposes no list endpoint (ADR-0003).
   * The snapshot is also what makes an extraction finishing in the background
   * show up without a poll.
   */
  subscribeToLibrary: (orgId: string) => () => void;
  uploadLibraryDocument: (orgId: string, file: File) => Promise<void>;
  deleteLibraryDocument: (orgId: string, docId: string) => Promise<void>;
  downloadLibraryDocument: (orgId: string, docId: string) => Promise<void>;
  clearLibraryError: () => void;
  unsubscribeLibrary: () => void;

  _librarySubscriptions: Map<string, () => void>;
}

/** Bytes the org currently holds. Pending rows carry no size and count as zero. */
export const usedLibraryBytes = (documents: LibraryDocument[]): number =>
  documents.reduce((total, document) => total + (document.size_bytes ?? 0), 0);

export const createLibrarySlice: StateCreator<
  StoreState,
  [],
  [],
  LibrarySlice
> = (set, get) => ({
  libraryDocuments: [],
  isLibraryLoading: false,
  libraryError: null,
  libraryUpload: null,
  _librarySubscriptions: new Map(),

  subscribeToLibrary: (orgId: string) => {
    if (!orgId) return () => {};

    const existing = get()._librarySubscriptions.get(orgId);
    if (existing) return existing;

    set((state) => ({
      isLibraryLoading: state.libraryDocuments.length === 0,
      libraryError: null,
    }));

    const libraryRef = collection(db, `organizations/${orgId}/library`);
    const libraryQuery = query(libraryRef, orderBy("uploaded_at", "desc"));

    const unsubscribe = onSnapshot(
      libraryQuery,
      (snapshot) => {
        const documents: LibraryDocument[] = [];
        snapshot.docs.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          const parsed = safeLibraryDocumentParse({
            ...data,
            id: docSnapshot.id,
            // A just-written document has SERVER_TIMESTAMP still resolving, so
            // its timestamp reads null for one snapshot.
            uploaded_at: data.uploaded_at?.toDate() ?? new Date(),
          });
          if (parsed.success) {
            documents.push(parsed.data);
          } else {
            console.error(
              `Invalid library document ${docSnapshot.id}`,
              parsed.error,
              data,
            );
            captureException(parsed.error, {
              extra: { docId: docSnapshot.id },
            });
          }
        });
        set({ libraryDocuments: documents, isLibraryLoading: false });
      },
      (error) => {
        console.error("Library subscription failed", error);
        set({ libraryError: error.message, isLibraryLoading: false });
      },
    );

    get()._librarySubscriptions.set(orgId, unsubscribe);
    return unsubscribe;
  },

  uploadLibraryDocument: async (orgId: string, file: File) => {
    // Client-side checks are UX, not enforcement — core re-checks both against
    // the stat'd object, where nothing is supplied by the caller.
    const contentType = contentTypeForFilename(file.name);
    if (!contentType) {
      set({
        libraryError: `${file.name} is not an accepted file type. Upload a PDF, PowerPoint, Markdown, text or image file.`,
      });
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      set({
        libraryError: `${file.name} is larger than the ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)}MB per-file limit.`,
      });
      return;
    }

    set({ libraryError: null, libraryUpload: { filename: file.name, fraction: 0 } });
    try {
      const ticket = await LibraryService.createUpload(
        orgId,
        file.name,
        contentType,
      );
      await LibraryService.uploadToSignedUrl(
        ticket.upload_url,
        file,
        contentType,
        (fraction) => set({ libraryUpload: { filename: file.name, fraction } }),
      );
      await LibraryService.finalizeUpload(orgId, ticket.doc_id);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "The upload failed.";
      set({ libraryError: message });
      captureException(error);
    } finally {
      set({ libraryUpload: null });
    }
  },

  deleteLibraryDocument: async (orgId: string, docId: string) => {
    set({ libraryError: null });
    try {
      await LibraryService.deleteDocument(orgId, docId);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "The delete failed.";
      set({ libraryError: message });
      captureException(error);
    }
  },

  downloadLibraryDocument: async (orgId: string, docId: string) => {
    set({ libraryError: null });
    try {
      const download = await LibraryService.getDownloadUrl(orgId, docId);
      // A signed URL carries its own credential and the response sets
      // Content-Disposition: attachment, so navigating to it downloads rather
      // than replacing the app.
      window.location.assign(download.download_url);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "The download failed.";
      set({ libraryError: message });
      captureException(error);
    }
  },

  clearLibraryError: () => set({ libraryError: null }),

  unsubscribeLibrary: () => {
    get()._librarySubscriptions.forEach((unsubscribe) => unsubscribe());
    get()._librarySubscriptions.clear();
    set({ libraryDocuments: [], isLibraryLoading: false, libraryError: null });
  },
});
